import { useRef, useState } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import type { ViewState, Zone } from "./warehouse-types";
import { Button } from "./ui";
import {
  IconZoomIn,
  IconZoomOut,
  IconHome,
  IconPointer,
  IconSquare,
  IconHandStop,
  IconPhoto,
  IconPhotoEdit,
  IconTrash,
  IconArrowsMove,
  IconChevronUp,
  IconInfoCircle,
  IconMapPin,
} from "@tabler/icons-react";

interface ToolbarProps {
  viewState: ViewState;
  zones: Zone[];
  hasFloorPlan: boolean;
  tool: "select" | "draw" | "floorplan";
  onToolChange: (tool: "select" | "draw" | "floorplan") => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomToWarehouse: () => void;
  onUploadFloorPlan: (url: string) => void;
  onClearFloorPlan: () => void;
}

export function Toolbar({
  viewState,
  zones,
  hasFloorPlan,
  tool,
  onToolChange,
  onZoomIn,
  onZoomOut,
  onZoomToWarehouse,
  onUploadFloorPlan,
  onClearFloorPlan,
}: ToolbarProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [instructionsOpen, setInstructionsOpen] = useState(true);
  // 目前位置 panel removed per request
  // const [currentInfoOpen, setCurrentInfoOpen] = useState(true);

  // const currentZone = viewState.zoneId
  //   ? zones.find((z) => z.id === viewState.zoneId)
  //   : null;

  // Tools only apply to the warehouse overview; keep the icons visible inside a
  // zone but disable them so the left toolbar layout stays consistent.
  const isWarehouseMode = viewState.mode === "warehouse";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const url = event.target?.result as string;
      onUploadFloorPlan(url);
    };
    reader.readAsDataURL(file);

    // Reset input so the same file can be selected again
    e.target.value = "";
  };

  const handleClearFloorPlan = () => {
    Modal.confirm({
      title: t("warehouseMap.toolbar.removeFloorPlan"),
      content: t("warehouseMap.toolbar.removeFloorPlanConfirm"),
      okText: t("warehouseMap.toolbar.remove"),
      cancelText: t("warehouseMap.cancel"),
      okButtonProps: { danger: true },
      onOk: onClearFloorPlan,
    });
  };

  return (
    <>
    <div className="absolute left-4 top-4 z-10 flex flex-col gap-1.5">
      {/* Floor plan controls — all base-image (底圖) related actions grouped together, kept at the top */}
      <div className="flex w-fit flex-col gap-0.5 rounded-lg border border-[#30333c] bg-[#1a1d24]/95 p-0.5 shadow-lg backdrop-blur">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={!isWarehouseMode}
          onClick={() => fileInputRef.current?.click()}
          title={
            isWarehouseMode
              ? hasFloorPlan
                ? t("warehouseMap.toolbar.replaceFloorPlan")
                : t("warehouseMap.toolbar.uploadFloorPlan")
              : t("warehouseMap.toolbar.floorPlanOverviewOnly")
          }
        >
          <IconPhoto className="h-3.5 w-3.5" />
        </Button>
        {hasFloorPlan && (
          <Button
            variant={isWarehouseMode && tool === "floorplan" ? "default" : "ghost"}
            size="icon"
            className="h-7 w-7"
            disabled={!isWarehouseMode}
            onClick={() => onToolChange("floorplan")}
            title={
              isWarehouseMode
                ? t("warehouseMap.toolbar.editFloorPlan")
                : t("warehouseMap.toolbar.editFloorPlanOverviewOnly")
            }
          >
            <IconPhotoEdit className="h-3.5 w-3.5" />
          </Button>
        )}
        {hasFloorPlan && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-red-400 hover:bg-red-500/10 hover:text-red-400"
            disabled={!isWarehouseMode}
            onClick={handleClearFloorPlan}
            title={
              isWarehouseMode
                ? t("warehouseMap.toolbar.removeFloorPlan")
                : t("warehouseMap.toolbar.removeFloorPlanOverviewOnly")
            }
          >
            <IconTrash className="h-3.5 w-3.5" />
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Tool selector */}
      <div className="flex w-fit flex-col gap-0.5 rounded-lg border border-[#30333c] bg-[#1a1d24]/95 p-0.5 shadow-lg backdrop-blur">
        <Button
          variant={isWarehouseMode && tool === "select" ? "default" : "ghost"}
          size="icon"
          className="h-7 w-7"
          disabled={!isWarehouseMode}
          onClick={() => onToolChange("select")}
          title={
            isWarehouseMode
              ? t("warehouseMap.toolbar.selectTool")
              : t("warehouseMap.toolbar.selectToolOverviewOnly")
          }
        >
          <IconPointer className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant={isWarehouseMode && tool === "draw" ? "default" : "ghost"}
          size="icon"
          className="h-7 w-7"
          disabled={!isWarehouseMode}
          onClick={() => onToolChange("draw")}
          title={
            isWarehouseMode
              ? t("warehouseMap.toolbar.drawZone")
              : t("warehouseMap.toolbar.drawZoneOverviewOnly")
          }
        >
          <IconSquare className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* View controls */}
      <div className="flex w-fit flex-col gap-0.5 rounded-lg border border-[#30333c] bg-[#1a1d24]/95 p-0.5 shadow-lg backdrop-blur">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onZoomIn}
          title={t("warehouseMap.toolbar.zoomIn")}
        >
          <IconZoomIn className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onZoomOut}
          title={t("warehouseMap.toolbar.zoomOut")}
        >
          <IconZoomOut className="h-3.5 w-3.5" />
        </Button>
        <div className="my-1 border-t border-[#30333c]" />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onZoomToWarehouse}
          title={t("warehouseMap.toolbar.backToOverview")}
        >
          <IconHome className="h-3.5 w-3.5" />
        </Button>
      </div>

    </div>

    {/* Info panels - collapsible, bottom-left corner */}
    <div className="absolute bottom-4 left-4 z-10 flex w-64 max-w-[calc(100%-2rem)] flex-col gap-1.5">
      {/* Current view info (目前位置) — removed per request, kept for reference
      <div className="overflow-hidden rounded-lg border border-[#30333c] bg-[#1a1d24]/95 shadow-lg backdrop-blur">
      <button
        type="button"
        onClick={() => setCurrentInfoOpen((open) => !open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-100"
        title={currentInfoOpen ? "收合目前位置" : "展開目前位置"}
      >
        <IconMapPin className="h-3.5 w-3.5 flex-shrink-0" />
        <span>目前位置</span>
        <IconChevronUp
          className={`ml-auto h-3.5 w-3.5 flex-shrink-0 transition-transform ${
            currentInfoOpen ? "" : "rotate-180"
          }`}
        />
      </button>
      {currentInfoOpen && (
        <div className="border-t border-[#30333c] px-3 py-2">
          <div className="font-medium text-zinc-100">
            {viewState.mode === "warehouse" ? (
              "倉庫總覽"
            ) : (
              <span className="text-teal-400">{currentZone?.name} 區</span>
            )}
          </div>
          <div className="mt-1 text-xs text-zinc-400">
            縮放: {Math.round(viewState.scale * 100)}%
          </div>
        </div>
      )}
    </div>
      */}

      {/* Instructions */}
      <div className="overflow-hidden rounded-lg border border-[#30333c] bg-[#1a1d24]/95 shadow-lg backdrop-blur">
      <button
        type="button"
        onClick={() => setInstructionsOpen((open) => !open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-100"
        title={
          instructionsOpen
            ? t("warehouseMap.toolbar.collapseInstructions")
            : t("warehouseMap.toolbar.expandInstructions")
        }
      >
        <IconInfoCircle className="h-3.5 w-3.5 flex-shrink-0" />
        <span>{t("warehouseMap.instructions")}</span>
        <IconChevronUp
          className={`ml-auto h-3.5 w-3.5 flex-shrink-0 transition-transform ${
            instructionsOpen ? "" : "rotate-180"
          }`}
        />
      </button>
      {instructionsOpen && (
        <div className="space-y-1.5 border-t border-[#30333c] px-3 py-2 text-xs text-zinc-400">
          {viewState.mode === "warehouse" ? (
            <>
              <div className="flex items-center gap-2">
                <IconPointer className="h-3 w-3 flex-shrink-0 text-teal-400" />
                <span>{t("warehouseMap.toolbar.instrSelectTool")}</span>
              </div>
              <div className="flex items-center gap-2">
                <IconSquare className="h-3 w-3 flex-shrink-0" />
                <span>{t("warehouseMap.toolbar.instrDrawTool")}</span>
              </div>
              {hasFloorPlan && (
                <div className="flex items-center gap-2">
                  <IconPhotoEdit className="h-3 w-3 flex-shrink-0" />
                  <span>{t("warehouseMap.toolbar.instrFloorPlanTool")}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <IconSquare className="h-3 w-3 flex-shrink-0 text-blue-400" />
                <span>{t("warehouseMap.toolbar.instrDragSlot")}</span>
              </div>
              <div className="flex items-center gap-2">
                <IconPointer className="h-3 w-3 flex-shrink-0" />
                <span>{t("warehouseMap.toolbar.instrClickSlot")}</span>
              </div>
              <div className="flex items-center gap-2">
                <IconSquare className="h-3 w-3 flex-shrink-0 text-teal-400" />
                <span>{t("warehouseMap.toolbar.instrClickZone")}</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-2">
            <IconArrowsMove className="h-3 w-3 flex-shrink-0" />
            <span>{t("warehouseMap.toolbar.instrMoveResize")}</span>
          </div>
          <div className="flex items-center gap-2">
            <IconZoomIn className="h-3 w-3 flex-shrink-0" />
            <span>{t("warehouseMap.toolbar.instrZoom")}</span>
          </div>
          <div className="flex items-center gap-2">
            <IconHandStop className="h-3 w-3 flex-shrink-0" />
            <span>{t("warehouseMap.toolbar.instrPan")}</span>
          </div>
        </div>
      )}
      </div>
    </div>
    </>
  );
}
